// frontend/src/utils/printUtils.js
import { formatDate, formatTime } from './dateUtils';

/**
 * Genera el HTML para imprimir una historia clínica
 * @param {Object} historia - Historia clínica a imprimir
 * @param {Object} options - Opciones adicionales (logo, nombre de clínica, etc.)
 * @returns {string} HTML formateado para impresión
 */
export const generateHistoriaClinicaHTML = (historia, options = {}) => {
  if (!historia || !historia.paciente) {
    return '<p>Error: No se puede generar el documento para impresión</p>';
  }

  const { paciente, especialista, especialidad } = historia;
  
  // Opciones con valores por defecto
  const opts = {
    clinicaNombre: options.clinicaNombre || 'NeuroPsico Cusco',
    clinicaDireccion: options.clinicaDireccion || 'Dirección de la clínica',
    clinicaTelefono: options.clinicaTelefono || 'Teléfono de contacto',
    clinicaEmail: options.clinicaEmail || 'correo@ejemplo.com',
    logoUrl: options.logoUrl || '/assets/logo.png',
    incluirSesiones: options.incluirSesiones !== false,
    titulo: options.titulo || 'Historia Clínica'
  };
  
  // Crear el HTML para impresión
  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Historia Clínica - ${paciente.nombres} ${paciente.apellidos}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.5;
          margin: 0;
          padding: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #5D1390;
        }
        .logo {
          max-height: 60px;
          margin-bottom: 10px;
        }
        h1, h2, h3, h4 {
          color: #2e59d9;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .section {
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f8f9fc;
          border-radius: 5px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .info-item {
          margin-bottom: 5px;
        }
        .label {
          font-weight: bold;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        table, th, td {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
        }
        .page-break {
          page-break-before: always;
        }
        @media print {
          body {
            padding: 0;
            font-size: 12px;
          }
          .no-print {
            display: none;
          }
          .page-break {
            page-break-before: always;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${opts.logoUrl}" alt="${opts.clinicaNombre}" class="logo">
        <h1>${opts.titulo}</h1>
        <p>Fecha de emisión: ${formatDate(new Date())}</p>
      </div>

      <div class="section">
        <h2>Información del Paciente</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Nombre:</span> ${paciente.nombres} ${paciente.apellidos}
          </div>
          <div class="info-item">
            <span class="label">N° Historia:</span> ${paciente.numero_historia}
          </div>
          <div class="info-item">
            <span class="label">DNI:</span> ${paciente.dni}
          </div>
          <div class="info-item">
            <span class="label">Fecha de Nacimiento:</span> ${formatDate(paciente.fecha_nacimiento)}
          </div>
          <div class="info-item">
            <span class="label">Edad:</span> ${paciente.edad} años
          </div>
          <div class="info-item">
            <span class="label">Sexo:</span> ${paciente.sexo === 'M' ? 'Masculino' : paciente.sexo === 'F' ? 'Femenino' : 'Otro'}
          </div>
          <div class="info-item">
            <span class="label">Teléfono:</span> ${paciente.telefono || 'No registrado'}
          </div>
          <div class="info-item">
            <span class="label">Email:</span> ${paciente.email || 'No registrado'}
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Información de la Historia Clínica</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Fecha de Creación:</span> ${formatDate(historia.fecha_creacion)}
          </div>
          <div class="info-item">
            <span class="label">Especialidad:</span> ${especialidad.nombre}
          </div>
          <div class="info-item">
            <span class="label">Especialista:</span> ${especialista.usuario.nombre} ${especialista.usuario.apellidos}
          </div>
          <div class="info-item">
            <span class="label">Estado:</span> ${historia.estado}
          </div>
          ${historia.fecha_finalizacion ? `
          <div class="info-item">
            <span class="label">Fecha de Finalización:</span> ${formatDate(historia.fecha_finalizacion)}
          </div>
          ` : ''}
        </div>
      </div>

      <div class="section">
        <h2>Motivo de Consulta</h2>
        <p>${historia.motivo_consulta || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Historia de la Enfermedad Actual</h2>
        <p>${historia.historia_enfermedad_actual || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Antecedentes</h2>
        <h3>Antecedentes Personales</h3>
        <p>${historia.antecedentes_personales || 'No registrado'}</p>
        
        <h3>Antecedentes Familiares</h3>
        <p>${historia.antecedentes_familiares || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Examen Mental</h2>
        <p>${historia.examen_mental || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Diagnóstico</h2>
        <p>${historia.diagnostico || 'No registrado'}</p>
        ${historia.diagnostico_cie10 ? `<p><strong>CIE-10:</strong> ${historia.diagnostico_cie10}</p>` : ''}
      </div>

      <div class="section">
        <h2>Plan de Tratamiento</h2>
        <p>${historia.plan_tratamiento || 'No registrado'}</p>
        
        ${historia.objetivos_terapeuticos ? `
        <h3>Objetivos Terapéuticos</h3>
        <p>${historia.objetivos_terapeuticos}</p>
        ` : ''}
        
        ${historia.num_sesiones_sugeridas ? `
        <p><strong>Sesiones sugeridas:</strong> ${historia.num_sesiones_sugeridas}
        ${historia.frecuencia_sesiones ? ` (${historia.frecuencia_sesiones})` : ''}</p>
        ` : ''}
        
        ${historia.pronostico ? `
        <p><strong>Pronóstico:</strong> ${historia.pronostico}</p>
        ` : ''}
      </div>

      ${historia.sesiones && historia.sesiones.length > 0 && opts.incluirSesiones ? `
      <div class="page-break"></div>
      <div class="section">
        <h2>Registro de Sesiones</h2>
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Especialista</th>
              <th>Estado</th>
              <th>Actividades</th>
            </tr>
          </thead>
          <tbody>
            ${historia.sesiones.map(sesion => `
            <tr>
              <td>${formatDate(sesion.fecha)}</td>
              <td>${sesion.especialista?.usuario?.nombre} ${sesion.especialista?.usuario?.apellidos}</td>
              <td>${sesion.estado}</td>
              <td>${sesion.actividades_realizadas || 'No registradas'}</td>
            </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ` : ''}

      ${historia.evaluaciones && historia.evaluaciones.length > 0 && opts.incluirEvaluaciones ? `
      <div class="page-break"></div>
      <div class="section">
        <h2>Evaluaciones Realizadas</h2>
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Especialista</th>
              <th>Funciones Evaluadas</th>
            </tr>
          </thead>
          <tbody>
            ${historia.evaluaciones.map(evaluacion => `
            <tr>
              <td>${formatDate(evaluacion.fecha_evaluacion)}</td>
              <td>${evaluacion.especialista?.usuario?.nombre} ${evaluacion.especialista?.usuario?.apellidos}</td>
              <td>${evaluacion.funciones_evaluadas || 'No especificadas'}</td>
            </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ` : ''}

      <div class="footer">
        <p>Este documento es confidencial y contiene información médica protegida.</p>
        <p>© ${new Date().getFullYear()} ${opts.clinicaNombre} - Todos los derechos reservados</p>
      </div>

      <div class="no-print" style="margin-top: 20px; text-align: center;">
        <button onclick="window.print()">Imprimir</button>
        <button onclick="window.close()">Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

/**
 * Abre una ventana de impresión para la historia clínica
 * @param {Object} historia - Historia clínica a imprimir
 * @param {Object} options - Opciones adicionales
 */
export const printHistoriaClinica = (historia, options = {}) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (printWindow) {
    printWindow.document.write(generateHistoriaClinicaHTML(historia, options));
    printWindow.document.close();
    
    // Dar tiempo para que se carguen los estilos y las imágenes
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  } else {
    alert('Por favor, permite las ventanas emergentes para imprimir.');
  }
};

/**
 * Genera el HTML para imprimir un reporte de evaluación
 * @param {Object} evaluacion - Evaluación a imprimir
 * @param {Object} options - Opciones adicionales
 * @returns {string} HTML formateado para impresión
 */
export const generateEvaluacionHTML = (evaluacion, options = {}) => {
  if (!evaluacion || !evaluacion.historia_clinica || !evaluacion.historia_clinica.paciente) {
    return '<p>Error: No se puede generar el documento para impresión</p>';
  }

  const { historia_clinica, especialista } = evaluacion;
  const { paciente } = historia_clinica;
  
  // Opciones con valores por defecto
  const opts = {
    clinicaNombre: options.clinicaNombre || 'NeuroPsico Cusco',
    clinicaDireccion: options.clinicaDireccion || 'Dirección de la clínica',
    clinicaTelefono: options.clinicaTelefono || 'Teléfono de contacto',
    clinicaEmail: options.clinicaEmail || 'correo@ejemplo.com',
    logoUrl: options.logoUrl || '/assets/logo.png',
    incluirPruebas: options.incluirPruebas !== false,
    titulo: options.titulo || 'Informe de Evaluación Neuropsicológica'
  };
  
  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Evaluación Neuropsicológica - ${paciente.nombres} ${paciente.apellidos}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.5;
          margin: 0;
          padding: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #5D1390;
        }
        .logo {
          max-height: 60px;
          margin-bottom: 10px;
        }
        h1, h2, h3, h4 {
          color: #2e59d9;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .section {
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f8f9fc;
          border-radius: 5px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .info-item {
          margin-bottom: 5px;
        }
        .label {
          font-weight: bold;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        table, th, td {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
        }
        .page-break {
          page-break-before: always;
        }
        @media print {
          body {
            padding: 0;
            font-size: 12px;
          }
          .no-print {
            display: none;
          }
          .page-break {
            page-break-before: always;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${opts.logoUrl}" alt="${opts.clinicaNombre}" class="logo">
        <h1>${opts.titulo}</h1>
        <p>Fecha de emisión: ${formatDate(new Date())}</p>
      </div>

      <div class="section">
        <h2>Información del Paciente</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Nombre:</span> ${paciente.nombres} ${paciente.apellidos}
          </div>
          <div class="info-item">
            <span class="label">N° Historia:</span> ${paciente.numero_historia}
          </div>
          <div class="info-item">
            <span class="label">DNI:</span> ${paciente.dni}
          </div>
          <div class="info-item">
            <span class="label">Fecha de Nacimiento:</span> ${formatDate(paciente.fecha_nacimiento)}
          </div>
          <div class="info-item">
            <span class="label">Edad:</span> ${paciente.edad} años
          </div>
          <div class="info-item">
            <span class="label">Sexo:</span> ${paciente.sexo === 'M' ? 'Masculino' : paciente.sexo === 'F' ? 'Femenino' : 'Otro'}
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Información de la Evaluación</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Fecha de Evaluación:</span> ${formatDate(evaluacion.fecha_evaluacion)}
          </div>
          <div class="info-item">
            <span class="label">Especialista:</span> ${especialista.usuario.nombre} ${especialista.usuario.apellidos}
          </div>
          <div class="info-item">
            <span class="label">Especialidad:</span> ${especialista.especialidades?.[0]?.nombre || 'No especificada'}
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Funciones Evaluadas</h2>
        <p>${evaluacion.funciones_evaluadas || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Instrumentos Utilizados</h2>
        <p>${evaluacion.instrumentos_utilizados || 'No registrado'}</p>
        
        ${evaluacion.pruebas_aplicadas && evaluacion.pruebas_aplicadas.length > 0 && opts.incluirPruebas ? `
        <h3>Pruebas Aplicadas</h3>
        <table>
          <thead>
            <tr>
              <th>Prueba</th>
              <th>Puntuación</th>
              <th>Interpretación</th>
            </tr>
          </thead>
          <tbody>
            ${evaluacion.pruebas_aplicadas.map(prueba => `
            <tr>
              <td>${prueba.prueba?.nombre || 'No especificada'}</td>
              <td>${prueba.puntuacion || 'No registrada'}</td>
              <td>${prueba.interpretacion || 'No registrada'}</td>
            </tr>
            `).join('')}
          </tbody>
        </table>
        ` : ''}
      </div>

      <div class="section">
        <h2>Resultados</h2>
        <p>${evaluacion.resultados || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Interpretación</h2>
        <p>${evaluacion.interpretacion || 'No registrado'}</p>
      </div>

      <div class="section">
        <h2>Recomendaciones</h2>
        <p>${evaluacion.recomendaciones || 'No registrado'}</p>
      </div>

      <div class="footer">
        <p>Este documento es confidencial y contiene información médica protegida.</p>
        <p>© ${new Date().getFullYear()} ${opts.clinicaNombre} - Todos los derechos reservados</p>
      </div>

      <div class="no-print" style="margin-top: 20px; text-align: center;">
        <button onclick="window.print()">Imprimir</button>
        <button onclick="window.close()">Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

/**
 * Abre una ventana de impresión para la evaluación
 * @param {Object} evaluacion - Evaluación a imprimir
 * @param {Object} options - Opciones adicionales
 */
export const printEvaluacion = (evaluacion, options = {}) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (printWindow) {
    printWindow.document.write(generateEvaluacionHTML(evaluacion, options));
    printWindow.document.close();
    
    // Dar tiempo para que se carguen los estilos y las imágenes
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  } else {
    alert('Por favor, permite las ventanas emergentes para imprimir.');
  }
};

/**
 * Genera el HTML para imprimir un reporte de sesiones
 * @param {Array} sesiones - Lista de sesiones a imprimir
 * @param {Object} paciente - Datos del paciente
 * @param {Object} options - Opciones adicionales
 * @returns {string} HTML formateado para impresión
 */
export const generateSesionesHTML = (sesiones, paciente, options = {}) => {
  if (!sesiones || !sesiones.length || !paciente) {
    return '<p>Error: No se puede generar el documento para impresión</p>';
  }
  
  // Opciones con valores por defecto
  const opts = {
    clinicaNombre: options.clinicaNombre || 'NeuroPsico Cusco',
    clinicaDireccion: options.clinicaDireccion || 'Dirección de la clínica',
    clinicaTelefono: options.clinicaTelefono || 'Teléfono de contacto',
    clinicaEmail: options.clinicaEmail || 'correo@ejemplo.com',
    logoUrl: options.logoUrl || '/assets/logo.png',
    titulo: options.titulo || 'Reporte de Sesiones Terapéuticas',
    periodo: options.periodo || 'Todas las sesiones'
  };
  
  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Reporte de Sesiones - ${paciente.nombres} ${paciente.apellidos}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.5;
          margin: 0;
          padding: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #5D1390;
        }
        .logo {
          max-height: 60px;
          margin-bottom: 10px;
        }
        h1, h2, h3, h4 {
          color: #2e59d9;
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .section {
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f8f9fc;
          border-radius: 5px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .info-item {
          margin-bottom: 5px;
        }
        .label {
          font-weight: bold;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        table, th, td {
          border: 1px solid #ddd;
        }
        th, td {
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
        }
        .sesion-detalle {
          margin-bottom: 15px;
          padding: 10px;
          border: 1px solid #ddd;
          background-color: #fff;
        }
        .sesion-header {
          display: flex;
          justify-content: space-between;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
          margin-bottom: 5px;
        }
        .badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 15px;
          font-size: 12px;
          font-weight: bold;
          background-color: #eee;
        }
        .badge-success { background-color: #d4edda; color: #155724; }
        .badge-primary { background-color: #cfe2ff; color: #0a58ca; }
        .badge-warning { background-color: #fff3cd; color: #856404; }
        .badge-danger { background-color: #f8d7da; color: #721c24; }
        @media print {
          body {
            padding: 0;
            font-size: 12px;
          }
          .no-print {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${opts.logoUrl}" alt="${opts.clinicaNombre}" class="logo">
        <h1>${opts.titulo}</h1>
        <p>Paciente: ${paciente.nombres} ${paciente.apellidos}</p>
        <p>Periodo: ${opts.periodo}</p>
        <p>Fecha de emisión: ${formatDate(new Date())}</p>
      </div>

      <div class="section">
        <h2>Información del Paciente</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Nombre:</span> ${paciente.nombres} ${paciente.apellidos}
          </div>
          <div class="info-item">
            <span class="label">N° Historia:</span> ${paciente.numero_historia}
          </div>
          <div class="info-item">
            <span class="label">DNI:</span> ${paciente.dni}
          </div>
          <div class="info-item">
            <span class="label">Fecha de Nacimiento:</span> ${formatDate(paciente.fecha_nacimiento)}
          </div>
          <div class="info-item">
            <span class="label">Edad:</span> ${paciente.edad} años
          </div>
          <div class="info-item">
            <span class="label">Sexo:</span> ${paciente.sexo === 'M' ? 'Masculino' : paciente.sexo === 'F' ? 'Femenino' : 'Otro'}
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Resumen de Sesiones</h2>
        <p>Total de sesiones: ${sesiones.length}</p>
        <p>Sesiones realizadas: ${sesiones.filter(s => s.estado === 'Realizada').length}</p>
        <p>Sesiones programadas: ${sesiones.filter(s => s.estado === 'Programada').length}</p>
        <p>Sesiones canceladas: ${sesiones.filter(s => s.estado === 'Cancelada').length}</p>
        <p>Inasistencias: ${sesiones.filter(s => s.estado === 'No asistió').length}</p>
      </div>

      <div class="section">
        <h2>Detalle de Sesiones</h2>
        
        ${sesiones.map((sesion, index) => `
        <div class="sesion-detalle">
          <div class="sesion-header">
            <div>
              <strong>Sesión ${index + 1} - ${formatDate(sesion.fecha)}</strong> | 
              ${formatTime(sesion.hora_inicio)} - ${formatTime(sesion.hora_fin)}
            </div>
            <div>
              <span class="badge badge-${
                sesion.estado === 'Realizada' ? 'success' : 
                sesion.estado === 'Programada' ? 'primary' : 
                sesion.estado === 'Cancelada' ? 'danger' : 
                'warning'
              }">${sesion.estado}</span>
            </div>
          </div>
          
          <p><strong>Especialista:</strong> ${sesion.especialista?.usuario?.nombre} ${sesion.especialista?.usuario?.apellidos}</p>
          
          ${sesion.notas ? `
          <p><strong>Notas:</strong> ${sesion.notas}</p>
          ` : ''}
          
          ${sesion.estado === 'Realizada' ? `
          <div>
            ${sesion.actividades_realizadas ? `
            <p><strong>Actividades realizadas:</strong> ${sesion.actividades_realizadas}</p>
            ` : ''}
            
            ${sesion.avances ? `
            <p><strong>Avances observados:</strong> ${sesion.avances}</p>
            ` : ''}
            
            ${sesion.tareas ? `
            <p><strong>Tareas asignadas:</strong> ${sesion.tareas}</p>
            ` : ''}
          </div>
          ` : ''}
        </div>
        `).join('')}
      </div>

      <div class="footer">
        <p>Este documento es confidencial y contiene información médica protegida.</p>
        <p>© ${new Date().getFullYear()} ${opts.clinicaNombre} - Todos los derechos reservados</p>
      </div>

      <div class="no-print" style="margin-top: 20px; text-align: center;">
        <button onclick="window.print()">Imprimir</button>
        <button onclick="window.close()">Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

/**
 * Abre una ventana de impresión para el reporte de sesiones
 * @param {Array} sesiones - Lista de sesiones a imprimir
 * @param {Object} paciente - Datos del paciente
 * @param {Object} options - Opciones adicionales
 */
export const printSesiones = (sesiones, paciente, options = {}) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (printWindow) {
    printWindow.document.write(generateSesionesHTML(sesiones, paciente, options));
    printWindow.document.close();
    
    // Dar tiempo para que se carguen los estilos y las imágenes
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  } else {
    alert('Por favor, permite las ventanas emergentes para imprimir.');
  }
};